/**
 * Pure progress reducer — mirror of
 * `services/prompt-orchestrator/app/v2/progress/reducer.py`.
 *
 * Both sides apply the same event sequence to the same shape; parity
 * tests in the backend lock in the contract. The frontend reducer runs
 * on incoming SSE `progress` events to keep the in-memory state map up
 * to date, and on the persisted `workspace_meta.progress` blob to
 * hydrate on session reload.
 *
 * Invariants:
 *   * Phase / task ordering is stable across mutations (replace, never
 *     reorder).
 *   * Counters and derived phase-state are recomputed after every
 *     mutation.
 *   * Unknown event kinds are dropped (forward-compat).
 *   * version bumps on every applied event so consumers can dedup.
 */
import {
  EVENT_KINDS,
  PHASE_IDS,
  PHASE_LABELS,
  ProgressEvent,
  ProgressPhase,
  ProgressState,
  ProgressTask,
  TaskState,
} from '../types/progress';

export function initialProgressState(): ProgressState {
  return { phases: [], version: 0, updated_at: null };
}

function findPhase(state: ProgressState, phaseId: string): ProgressPhase | undefined {
  return state.phases.find((p) => p.id === phaseId);
}

function findTask(phase: ProgressPhase, taskId: string): ProgressTask | undefined {
  return phase.tasks.find((t) => t.id === taskId);
}

function makePhase(phaseId: string, label?: string | null): ProgressPhase {
  return {
    id: phaseId,
    label: label || PHASE_LABELS[phaseId as keyof typeof PHASE_LABELS] || phaseId,
    state: 'queued',
    tasks: [],
    total: 0,
    done: 0,
    running: 0,
    failed: 0,
  };
}

function ensurePhase(
  state: ProgressState,
  phaseId: string,
  label?: string | null,
): ProgressPhase {
  const existing = findPhase(state, phaseId);
  if (existing) {
    if (label && !existing.label) existing.label = label;
    return existing;
  }
  const phase = makePhase(phaseId, label);
  // Insert in canonical PHASE_IDS order — rendering stays stable
  // regardless of emission order.
  const canonicalIndex = (PHASE_IDS as readonly string[]).indexOf(phaseId);
  const insertIndex = canonicalIndex === -1 ? PHASE_IDS.length : canonicalIndex;

  let inserted = false;
  const next: ProgressPhase[] = [];
  for (const existingPhase of state.phases) {
    const existingIndex = (PHASE_IDS as readonly string[]).indexOf(existingPhase.id);
    const eIdx = existingIndex === -1 ? PHASE_IDS.length : existingIndex;
    if (!inserted && insertIndex < eIdx) {
      next.push(phase);
      inserted = true;
    }
    next.push(existingPhase);
  }
  if (!inserted) next.push(phase);
  state.phases = next;
  return phase;
}

function ensureTask(
  phase: ProgressPhase,
  taskId: string,
  label?: string | null,
): ProgressTask {
  const existing = findTask(phase, taskId);
  if (existing) {
    if (label && !existing.label) existing.label = label;
    return existing;
  }
  const task: ProgressTask = {
    id: taskId,
    label: label || taskId,
    state: 'queued',
  };
  phase.tasks.push(task);
  return task;
}

function recomputeCounters(phase: ProgressPhase): void {
  const tasks = phase.tasks;
  const total = tasks.length;
  const done = tasks.filter((t) => t.state === 'done').length;
  const running = tasks.filter((t) => t.state === 'running').length;
  const failed = tasks.filter((t) => t.state === 'failed').length;
  phase.total = total;
  phase.done = done;
  phase.running = running;
  phase.failed = failed;

  // Dominance: failed > needs_input > running > done(all) > queued.
  if (tasks.some((t) => t.state === 'failed')) phase.state = 'failed';
  else if (tasks.some((t) => t.state === 'needs_input')) phase.state = 'needs_input';
  else if (running > 0) phase.state = 'running';
  else if (total > 0 && done === total) phase.state = 'done';
  else if (phase.state !== 'running' && phase.state !== 'done') phase.state = 'queued';
}

const VALID_KINDS = new Set<string>(EVENT_KINDS as readonly string[]);

export function applyProgressEvent(
  state: ProgressState,
  event: ProgressEvent,
): ProgressState {
  if (!VALID_KINDS.has(event.kind)) return state; // forward-compat drop
  if (!event.phase_id) return state;

  const phase = ensurePhase(state, event.phase_id, event.label ?? undefined);

  switch (event.kind) {
    case 'phase_start':
      if (phase.state === 'queued') phase.state = 'running';
      break;
    case 'phase_done':
      phase.state = 'done';
      break;
    case 'task_start': {
      if (!event.task_id) return state;
      const task = ensureTask(phase, event.task_id, event.label ?? undefined);
      if (task.state !== 'done' && task.state !== 'failed') task.state = 'running';
      delete task.reason;
      recomputeCounters(phase);
      break;
    }
    case 'task_progress': {
      if (!event.task_id) return state;
      const task = ensureTask(phase, event.task_id, event.label ?? undefined);
      if (task.state === 'queued') task.state = 'running';
      if (event.activity !== undefined && event.activity !== null) {
        task.activity = event.activity;
      }
      recomputeCounters(phase);
      break;
    }
    case 'task_done': {
      if (!event.task_id) return state;
      const task = ensureTask(phase, event.task_id, event.label ?? undefined);
      task.state = 'done';
      delete task.activity;
      delete task.reason;
      if (event.metrics) task.metrics = event.metrics;
      recomputeCounters(phase);
      break;
    }
    case 'task_failed': {
      if (!event.task_id) return state;
      const task = ensureTask(phase, event.task_id, event.label ?? undefined);
      task.state = 'failed';
      delete task.activity;
      if (event.reason !== undefined && event.reason !== null) task.reason = event.reason;
      recomputeCounters(phase);
      break;
    }
    case 'task_needs_input': {
      if (!event.task_id) return state;
      const task = ensureTask(phase, event.task_id, event.label ?? undefined);
      task.state = 'needs_input';
      delete task.activity;
      if (event.reason !== undefined && event.reason !== null) task.reason = event.reason;
      recomputeCounters(phase);
      break;
    }
  }

  state.version = (state.version ?? 0) + 1;
  state.updated_at = new Date().toISOString();
  return state;
}

/**
 * Apply many events in order. Mutates and returns state.
 * Convenience for replaying a batch (e.g. hydration buffer flush).
 */
export function applyProgressEvents(
  state: ProgressState,
  events: ProgressEvent[],
): ProgressState {
  for (const e of events) applyProgressEvent(state, e);
  return state;
}

/** Treat-as-immutable shallow snapshot for React state setters. */
export function snapshotProgressState(state: ProgressState): ProgressState {
  return {
    version: state.version,
    updated_at: state.updated_at,
    phases: state.phases.map((p) => ({
      ...p,
      tasks: p.tasks.map((t) => ({ ...t })),
    })),
  };
}

/**
 * Locate the currently-active task (running) across all phases. The
 * status strip uses this to populate its single-line heartbeat.
 * Preference: running task on the latest active phase.
 */
export function currentRunningTask(state: ProgressState):
  | { phase: ProgressPhase; task: ProgressTask }
  | null {
  // Walk phases in reverse canonical order — the most-recently-started
  // phase is the most relevant one to display.
  for (let i = state.phases.length - 1; i >= 0; i -= 1) {
    const phase = state.phases[i];
    if (phase.state !== 'running') continue;
    const task = phase.tasks.find((t) => t.state === 'running');
    if (task) return { phase, task };
  }
  // Fall back to needs_input as the next priority (something is
  // blocking and deserves the strip).
  for (let i = state.phases.length - 1; i >= 0; i -= 1) {
    const phase = state.phases[i];
    const task = phase.tasks.find((t) => t.state === 'needs_input');
    if (task) return { phase, task };
  }
  return null;
}

/** Aggregate completion across phases (used by status strip percent). */
export function overallProgress(state: ProgressState): {
  done: number;
  total: number;
  pct: number;
} {
  const done = state.phases.reduce((acc, p) => acc + p.done, 0);
  const total = state.phases.reduce((acc, p) => acc + p.total, 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return { done, total, pct };
}

// Re-export TaskState only as a value for convenience.
export type { TaskState };
